import React, {FC, useEffect, useState} from "react"
import Card from "@irati/ui/src/Layout/Card"
import {H2, H3, H4, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {FormattedCurrency, FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {Hr} from "@irati/ui/src/Layout"
import TreatsTriggerModal from "@irati-web/wallet/src/components/TreatsModal/TreatsTriggerModal"
import authentication from "@irati/auth"
import {PaymentCurrencyType, Wallet, WalletEntityType, WellnessMedia} from "@irati/types"
import mainTheme from "@irati/ui/src/global/theme"
import Link, {ButtonLink} from "@irati/ui/src/Link"
import walletService from "@irati/wallet/graph/wallet"
import Loading from "@irati/ui/src/Loading"
import ErrorText from "@irati/ui/src/ErrorText"
import {getFormattedRentalPeriodUnit} from "@irati/wellness/utils"

interface Props extends InjectIntl {
  media: WellnessMedia
}

const MissingWalletErrorComponent = (
  <Flex justifyContent="center">
    <ErrorText>
      <FormattedMessage id="wellness.rentalMedia.redemption.missingFPWallet" />
    </ErrorText>
  </Flex>
)

const RentStaticMedia: FC<Props> = ({media, formatMessage}) => {
  const [wallet, setWallet] = useState<Wallet>({})
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const {uuid, price, rentalPeriod, rentalPeriodUnit} = media

  const fetchWallet = async () => {
    const data = await walletService.get(authentication.user().uuid, PaymentCurrencyType.Tt, WalletEntityType.User, {
      fetchPolicy: "network-only"
    })
    setWallet(data)
    return data
  }

  useEffect(() => {
    setLoad(fetchWallet())
  }, [])

  return (
    <Card flush marginTop={5} paddingBottom={2}>
      <Flex justifyContent="space-between" alignItems="center" padding={[2, 2, 0, 2]}>
        <H4>
          <FormattedMessage id="wellness.rentalMedia.premiumContent" />
        </H4>
        <Flex direction="column" alignItems="flex-end">
          <Label>
            <FormattedMessage id="wellness.rentalMedia.price" />
          </Label>
          <H3>
            <FormattedCurrency value={price} isCents={false} currency={PaymentCurrencyType.Tt} />
          </H3>
        </Flex>
      </Flex>
      <Flex direction="column" padding={[2, 2, 0, 2]}>
        <Hr />
        <Label large marginBottom={3}>
          <FormattedMessage id="wellness.rentalMedia.static.body1" />
        </Label>
        <Label large>
          <FormattedMessage
            id="wellness.rentalMedia.static.body2"
            values={{duration: rentalPeriod, durationType: getFormattedRentalPeriodUnit(rentalPeriod, rentalPeriodUnit)}}
          />
        </Label>
        <Hr />
        <Flex justifyContent="center" marginBottom="20px">
          <Link cta href={formatMessage({id: "wellness.rentalMedia.redemption.termsAndConditions.link.href"})} target="_blank">
            <FormattedMessage id="platform.footer.href.termsAndConditions.text" />
          </Link>
        </Flex>
      </Flex>

      <Loading load={load} Error={() => MissingWalletErrorComponent}>
        <Flex style={{background: mainTheme.colors.utility.highlight}} direction="column" alignItems="center" marginBottom={2}>
          <H4 marginBottom={2} marginTop={2}>
            <FormattedMessage id="wellness.rentalMedia.yourPerkboxWallet" />
          </H4>
          <Flex direction="column" alignItems="center">
            <Label>
              <FormattedMessage id="wellness.rentalMedia.available" />
            </Label>
            <Flex>
              <H2>
                <FormattedCurrency value={wallet.availableBalance} isCents={false} currency={wallet.currency} />
              </H2>
              <TreatsTriggerModal />
            </Flex>
          </Flex>
          <ButtonLink primary marginTop={2} marginBottom={2} href={`${uuid}/pay`}>
            <FormattedMessage id="wellness.rentalMedia.static.purchase.cta" values={{price}} />
          </ButtonLink>
        </Flex>
      </Loading>
    </Card>
  )
}

export default injectIntl(RentStaticMedia)
